import React, { useCallback, useState, useEffect, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { useOV25UI } from '../../contexts/ov25-ui-context.js';
import {
  closeModuleSelectMenu,
} from '../../utils/configurator-utils.js';
import { VariantsHeader } from './VariantsHeader.js';
import { Snap2VariantSheetColumn } from '../Snap2VariantSheetColumn.js';
import { Snap2ModulesOptionBody } from './Snap2ModulesOptionBody.js';
import { ProductVariantsWrapper } from './ProductVariantsWrapper.js';
import { cn } from '../../lib/utils.js';

export interface Snap2WrapperProps {
  isInline?: boolean;
  embeddedInVariantsOnlySheet?: boolean;
  snap2ModulesOnlySideSheet?: boolean;
}

type Snap2PiecesOptionsLayoutProps = Pick<Snap2WrapperProps, 'isInline' | 'embeddedInVariantsOnlySheet'>;

type Snap2PiecesPanelProps = {
  isInline: boolean;
  embeddedInVariantsOnlySheet: boolean;
  hideVariantsHeader?: boolean;
  onCloseHeaderOverride?: () => void;
};

function Snap2PiecesPanel({
  isInline,
  embeddedInVariantsOnlySheet,
  hideVariantsHeader = false,
  onCloseHeaderOverride,
}: Snap2PiecesPanelProps) {
  const {
    isVariantsOpen,
    setIsVariantsOpen,
    isMobile,
    compatibleModules,
    configuratorState,
  } = useOV25UI();

  const handleClosePieces = () => {
    closeModuleSelectMenu();
    setIsVariantsOpen(false);
  };

  if (isMobile && !isInline) {
    return (
      <div className="ov:md:flex ov:md:flex-col ov:max-h-full ov:h-full ov:md:bg-(--ov25-background-color) ov:xl:border-(--ov25-border-color) ov:w-full">
        <VariantsHeader onCloseButtonClick={handleClosePieces} />
        <div className="ov:flex ov:min-h-0 ov:flex-1 ov:flex-col ov:overflow-hidden ov:px-2 ov:pb-4">
          <Snap2ModulesOptionBody />
        </div>
      </div>
    );
  }

  return (
    <div
      className="ov25-snap2-pieces-panel ov:flex ov:flex-col ov:flex-1 ov:min-h-0 ov:overflow-hidden ov:bg-(--ov25-background-color)"
      data-ov25-snap2-pieces-panel="true"
    >
      {!embeddedInVariantsOnlySheet && !isInline && !hideVariantsHeader && (
        <VariantsHeader onCloseButtonClick={onCloseHeaderOverride} />
      )}
      <div className="ov:flex-1 ov:min-h-0 ov:overflow-y-auto ov:md:px-0">
        <Snap2ModulesOptionBody />
      </div>
    </div>
  );
}

function Snap2ModulesOnlySheetInner({
  isInline = false,
  embeddedInVariantsOnlySheet = false,
}: Pick<Snap2WrapperProps, 'isInline' | 'embeddedInVariantsOnlySheet'>) {
  const { setIsModulePanelOpen } = useOV25UI();
  const handleCloseModulesSideSheetHeader = useCallback(() => {
    setIsModulePanelOpen(false);
    closeModuleSelectMenu();
  }, [setIsModulePanelOpen]);

  return (
    <Snap2PiecesPanel
      isInline={isInline}
      embeddedInVariantsOnlySheet={embeddedInVariantsOnlySheet}
      onCloseHeaderOverride={handleCloseModulesSideSheetHeader}
    />
  );
}

function Snap2PiecesOptionsLayout({
  isInline = false,
  embeddedInVariantsOnlySheet = false,
}: Snap2PiecesOptionsLayoutProps) {
  const { activeOptionId, setActiveOptionId, variantPanelOptions, snap2ModulesEmbedInVariantSheet, getString } =
    useOV25UI();
  const [segment, setSegment] = useState<'pieces' | 'options'>('options');
  const tabsIndicatorRef = useRef<HTMLDivElement>(null);
  const piecesTabRef = useRef<HTMLButtonElement>(null);
  const optionsTabRef = useRef<HTMLButtonElement>(null);
  const hasPositionedIndicatorRef = useRef(false);

  useEffect(() => {
    if (activeOptionId !== 'modules') return;
    if (snap2ModulesEmbedInVariantSheet) {
      setSegment('pieces');
    }
    const next = variantPanelOptions[0]?.id;
    if (next) setActiveOptionId(next);
  }, [activeOptionId, variantPanelOptions, setActiveOptionId, snap2ModulesEmbedInVariantSheet]);

  const tabBtn = (active: boolean) =>
    `ov25-filter-pill ov25-tabs-button ov:flex ov:flex-1 ov:min-w-0 ov:flex-row ov:items-center ov:justify-center ov:px-5 ov:py-2 ov:text-sm ov:font-semibold ov:rounded-full ov:whitespace-nowrap ov:cursor-pointer ov:transition-colors ${
      active
        ? 'ov:text-gray-800'
        : 'ov:text-gray-500 hover:ov:text-gray-700'
    }`;

  const syncTabsIndicator = useCallback(
    (instant = false) => {
      const indicator = tabsIndicatorRef.current;
      const activeTab = segment === 'pieces' ? piecesTabRef.current : optionsTabRef.current;
      if (!indicator || !activeTab) return;
      const nextX = activeTab.offsetLeft;
      const nextWidth = activeTab.offsetWidth;
      gsap.killTweensOf(indicator);
      if (instant) {
        gsap.set(indicator, { x: nextX, width: nextWidth, opacity: 1 });
        return;
      }
      gsap.to(indicator, {
        x: nextX,
        width: nextWidth,
        duration: 0.4,
        ease: 'power3.out',
      });
    },
    [segment]
  );

  useLayoutEffect(() => {
    const shouldSetInstantly = !hasPositionedIndicatorRef.current;
    syncTabsIndicator(shouldSetInstantly);
    hasPositionedIndicatorRef.current = true;
  }, [syncTabsIndicator]);

  useEffect(() => {
    const handleResize = () => syncTabsIndicator(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [syncTabsIndicator]);

  const hoistVariantsHeader =
    snap2ModulesEmbedInVariantSheet && !embeddedInVariantsOnlySheet && !isInline;

  if (!snap2ModulesEmbedInVariantSheet) {
    return (
      <div
        id="ov25-snap2-options-layout"
        className={`ov:flex ov:flex-col ov:max-h-full ov:h-full ov:min-h-0 ov:bg-(--ov25-background-color) ${isInline ? 'ov:flex-1 ov:overflow-hidden' : ''}`}
      >
        <div className="ov:flex-1 ov:min-h-0 ov:flex ov:flex-col ov:overflow-hidden">
          <ProductVariantsWrapper isInline={isInline} embeddedInVariantsOnlySheet={embeddedInVariantsOnlySheet} />
        </div>
      </div>
    );
  }

  return (
    <div
      id="ov25-snap2-options-layout"
      className={`ov:flex ov:flex-col ov:max-h-full ov:h-full ov:min-h-0 ov:bg-(--ov25-background-color) ${isInline ? 'ov:flex-1 ov:overflow-hidden' : ''}`}
    >
      {hoistVariantsHeader ? <VariantsHeader /> : null}
      <div
        id="ov25-snap2-options-wrapper"
        className="ov:shrink-0 ov:flex ov:w-full ov:max-w-full ov:items-center ov:justify-center ov:py-1 ov:px-2 ov:md:px-2 ov:md:pt-4 ov:md:pb-3 ov:bg-(--ov25-background-color)"
        role="tablist"
      >
        <div className="ov:relative ov:flex ov:w-full ov:bg-gray-200/50 ov:p-1 ov:rounded-full">
        <div
          ref={tabsIndicatorRef}
          className="ov25-filter-pill ov25-tabs-button ov:absolute ov:bg-(--ov25-background-color) ov:top-1 ov:bottom-1 ov:left-0 ov:rounded-full ov:pointer-events-none"
          data-ov25-tab-indicator="true"
          data-ov25-tab-active="true"
          data-selected="true"
          aria-hidden="true"
        />
        <button
          ref={piecesTabRef}
          type="button"
          role="tab"
          aria-selected={segment === 'pieces'}
          className={cn(tabBtn(segment === 'pieces'), 'ov:relative ov:z-10')}
          onClick={() => setSegment('pieces')}
          data-ov25-snap2-primary-segment-tab="pieces"
          data-selected={segment === 'pieces' ? 'true' : 'false'}
        >
          {getString('optionHeader', { OPTION_NAME: 'Pieces' }, 'Pieces')}
        </button>
        <button
          ref={optionsTabRef}
          type="button"
          role="tab"
          aria-selected={segment === 'options'}
          className={cn(tabBtn(segment === 'options'), 'ov:relative ov:z-10')}
          onClick={() => setSegment('options')}
          data-ov25-snap2-primary-segment-tab="options"
          data-selected={segment === 'options' ? 'true' : 'false'}
        >
          {getString('optionHeader', { OPTION_NAME: 'Finish' }, 'Finish')}
        </button>
        </div>
      </div>
      <div className="ov:flex-1 ov:min-h-0 ov:flex ov:flex-col ov:overflow-hidden">
        {segment === 'options' ? (
          <ProductVariantsWrapper
            isInline={isInline}
            embeddedInVariantsOnlySheet={embeddedInVariantsOnlySheet}
            hideVariantsHeader={hoistVariantsHeader}
          />
        ) : (
          <Snap2PiecesPanel
            isInline={isInline}
            embeddedInVariantsOnlySheet={embeddedInVariantsOnlySheet}
            hideVariantsHeader={hoistVariantsHeader}
          />
        )}
      </div>
    </div>
  );
}

export function Snap2Wrapper(props: Snap2WrapperProps) {
  if (props.snap2ModulesOnlySideSheet) {
    return (
      <Snap2VariantSheetColumn>
        <Snap2ModulesOnlySheetInner
          isInline={props.isInline}
          embeddedInVariantsOnlySheet={props.embeddedInVariantsOnlySheet}
        />
      </Snap2VariantSheetColumn>
    );
  }
  return (
    <Snap2VariantSheetColumn>
      <Snap2PiecesOptionsLayout
        isInline={props.isInline}
        embeddedInVariantsOnlySheet={props.embeddedInVariantsOnlySheet}
      />
    </Snap2VariantSheetColumn>
  );
}
